import type { PaymentMode } from './payment.ts';

/**
 * One bill paid two or three ways — part cash, part UPI or card (#357,
 * blossom-rest-api#560).
 *
 * **Called a split payment, never a split party.** On the gate a "split party"
 * is a basket split between an invoice and tickets; this is one bill's money
 * split between modes. Settled in full at the counter: nothing is owed after.
 *
 * The rules are the API's, held here so the screen, the main process and the
 * paper apply one copy: one part per mode, so two or three parts; each a whole
 * number of paise above nothing; and the parts add up to the bill exactly,
 * after any discount and the round off. A reference rides on a UPI or card
 * part, never on cash.
 */

/** One part of a split payment. Whole paise (Principle VI). */
export interface SalePaymentPart {
  readonly paymentMode: PaymentMode;
  readonly amountPaise: number;
  /** A UPI transaction id or a card approval code. Never on cash; absent where none was given. */
  readonly paymentReference?: string;
}

/**
 * How one sale is paid, as a request carries it: one mode as it always was, or
 * a split of two or three parts. Spread into a `SaleRecordRequest`.
 */
export type SalePayment =
  | { readonly paymentMode: PaymentMode; readonly paymentReference?: string }
  | { readonly payments: readonly SalePaymentPart[] };

/** One part per mode, so never more than this. */
export const SPLIT_PARTS_MAX = 3;

/** The order the API returns parts in, and the order a bill fills from them. */
const PAYMENT_ORDER: readonly PaymentMode[] = ['CASH', 'UPI', 'CARD'];

/** The parts in the API's order — cash, UPI, card — whatever order they were given in. */
export const inPaymentOrder = (parts: readonly SalePaymentPart[]): SalePaymentPart[] =>
  [...parts].sort(
    (a, b) => PAYMENT_ORDER.indexOf(a.paymentMode) - PAYMENT_ORDER.indexOf(b.paymentMode),
  );

/**
 * Whether these parts are a split this bill can be paid with: two or three of
 * them, one per mode, each a whole number of paise above nothing, adding up to
 * `totalPaise` exactly. The reference rule is not asked here; it has its own
 * refusal.
 */
export function isSplitOf(parts: readonly SalePaymentPart[], totalPaise: number): boolean {
  if (parts.length < 2 || parts.length > SPLIT_PARTS_MAX) return false;

  const modes = new Set(parts.map((part) => part.paymentMode));

  if (modes.size !== parts.length || [...modes].some((mode) => !PAYMENT_ORDER.includes(mode))) {
    return false;
  }

  if (parts.some((part) => !Number.isSafeInteger(part.amountPaise) || part.amountPaise <= 0)) {
    return false;
  }

  return parts.reduce((sum, part) => sum + part.amountPaise, 0) === totalPaise;
}

/**
 * The one mode a split sale is filed under where there is room for one: the
 * largest part's, a tie going to the earlier of cash, UPI, card — the API's own
 * rule for `paymentMode` on a split sale (#560). A summary, never a figure to
 * count a drawer by.
 */
export function headlineMode(parts: readonly SalePaymentPart[]): PaymentMode {
  const [first, ...rest] = inPaymentOrder(parts);

  if (first === undefined) throw new Error('a split payment has at least one part');

  return rest.reduce((best, part) => (part.amountPaise > best.amountPaise ? part : best), first)
    .paymentMode;
}

/**
 * What of this bill was paid in cash, or `null` where none was — what change is
 * worked out against, and whether the drawer opens.
 */
export function cashPaise(payment: SalePayment, totalPaise: number): number | null {
  if (!('payments' in payment)) return payment.paymentMode === 'CASH' ? totalPaise : null;

  return payment.payments.find((part) => part.paymentMode === 'CASH')?.amountPaise ?? null;
}

/** A sale's share of the parts, as the request will carry it. */
function asPayment(parts: readonly SalePaymentPart[], idle: SalePaymentPart): SalePayment {
  // A sale of ₹0 takes nothing from any part, and the API takes it as a plain
  // sale: there is no part of more than 0 to send.
  if (parts.length === 0) return { paymentMode: idle.paymentMode };

  const [only] = parts;

  // **One part is a plain sale**, and is sent exactly as one: `paymentMode`
  // and its reference, never a one-part `payments`.
  if (parts.length === 1 && only !== undefined) {
    return {
      paymentMode: only.paymentMode,
      ...(only.paymentReference === undefined ? {} : { paymentReference: only.paymentReference }),
    };
  }

  return { payments: parts };
}

/**
 * **One payment across several sales** — a party across outlets is a sale per
 * outlet (#284) — allocated in bill order: the first bill fills from the first
 * part, cash before UPI before card, and what is left of a part carries to the
 * next bill. Each sale's share adds up to that sale's own total.
 *
 * A part that spans two bills goes on both, reference and all: one UPI
 * transaction paid for both.
 *
 * `null` where the parts do not add up to the bills between them, or are not a
 * split at all. Pure, and integers throughout.
 */
export function allocateSplit(
  parts: readonly SalePaymentPart[],
  totals: readonly number[],
): SalePayment[] | null {
  const owed = totals.reduce((sum, total) => sum + total, 0);

  if (
    !isSplitOf(parts, owed) ||
    totals.some((total) => !Number.isSafeInteger(total) || total < 0)
  ) {
    return null;
  }

  const queue = inPaymentOrder(parts);
  const left = queue.map((part) => part.amountPaise);
  let at = 0;
  const shares: SalePayment[] = [];

  for (const total of totals) {
    const taken: SalePaymentPart[] = [];
    let due = total;

    while (due > 0) {
      const part = queue[at];
      const remaining = left[at];

      // Unreachable: the parts add up to the bills, checked above.
      if (part === undefined || remaining === undefined) return null;

      const amountPaise = Math.min(due, remaining);

      taken.push({ ...part, amountPaise });
      left[at] = remaining - amountPaise;
      due -= amountPaise;

      if (left[at] === 0) at += 1;
    }

    shares.push(asPayment(taken, queue[Math.min(at, queue.length - 1)] ?? queue[0]!));
  }

  return shares;
}
